import { defineStore } from 'pinia';
import { onScopeDispose, ref } from 'vue';
import type { LocationQueryValue, RouteLocationNormalized, RouteLocationRaw } from 'vue-router';
import { personService } from '~/services/person-service';

import { useVaultStore } from '~/stores/person/vault/vault';
import { ModalNames } from '~/types/modals';
import { ApiError, memberFacingMessage } from '~/utils/errors';
import { addressCarriesPerson } from '~/utils/person-address';
import type { Address, Aka, License, Person } from '~/types/person/person';
import type { EmailVerificationData } from '~/types/person/vault';

/**
 * Rule K3 — impersonation target eligibility (single source of truth).
 *
 * Impersonation re-scopes the app to another person's identity vault via the
 * `?person=<id>` URL param, so a target must be one the caller is genuinely
 * authorized to act for:
 *   - a licensed consumer (a managed/family account the caller licenses), or
 *   - a business employee (B2B) — the employer relationship authorizes the
 *     handoff whether or not the employee holds a paid license.
 * A bare unlicensed non-employee (a name+email anyone could add) is never a
 * valid target. The backend person-fetch is the authoritative gate; this
 * predicate keeps the employee details drawer from offering an "impersonate"
 * action the API would reject.
 */
export function canImpersonateTarget(target: { isBusinessEmployee?: boolean; licensed?: boolean }): boolean {
    return Boolean(target.isBusinessEmployee || target.licensed);
}

/** The person a `?person` value names, or null when it names no one. */
function personIdNamedBy(param: LocationQueryValue | LocationQueryValue[] | undefined): number | null {
    const personId = typeof param === 'string' ? Number.parseInt(param, 10) : Number.NaN;
    return Number.isNaN(personId) ? null : personId;
}

/**
 * What a re-read answers the calls it superseded: whether the record on screen was replaced, and the
 * failure of the call that owns those figures, when that is how it ended. Both travel the chain,
 * because a waiter needs the owner's answer and not merely the fact that its own read was overtaken.
 */
type RereadOutcome = { assigned: boolean; error?: unknown };

export const usePersonStore = defineStore('person', () => {
    const { showSuccess, showError, showWarning } = useGlobalToast();
    const person = ref<Person>();
    const auth = useAuthStore();
    const vaultStore = useVaultStore();
    const router = useRouter();
    const impersonationLoader = ref(false);
    const errorMessage = ref('');
    const imgViewerUrl = ref<string | null>(null);
    const selectedPersonForLicense = ref<License | null>(null);

    /**
     * Check if currently impersonating by comparing loaded person to authenticated user
     * Returns the person ID if impersonating, or false if not
     */
    const impersonating = computed(() => {
        const actualUserId = auth.user?.identity?.id;
        const loadedPersonId = person.value?.id;

        if (!actualUserId || !loadedPersonId) {
            return false;
        }

        // Impersonating if the loaded person is different from the authenticated user
        return loadedPersonId !== actualUserId ? loadedPersonId : false;
    });

    /**
     * End impersonation by removing the person query parameter
     */
    async function endImpersonation() {
        impersonationLoader.value = true;

        // Clear vault data when ending impersonation
        vaultStore.clearVaultData();

        // Get current path and remove person query parameter
        const currentPath = window.location.pathname;
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete('person');

        // Build new URL without person param
        const newSearch = searchParams.toString();
        const newUrl = newSearch ? `${currentPath}?${newSearch}` : currentPath;

        // Reload the page without the URL parameter
        window.location.replace(newUrl);

        // No code after this will be executed.
    }

    /**
     * End impersonation (if any) while switching context to a new path.
     *
     * Swapping between the personal and business portals must ALWAYS drop
     * impersonation. A hard navigation guarantees the `?person` param is gone
     * and the app re-bootstraps as the real authenticated user — plugins/hooks.ts
     * re-fetches the person on load based on the (now absent) URL param.
     */
    function endImpersonationTo(path: string) {
        impersonationLoader.value = true;

        vaultStore.clearVaultData();

        person.value = undefined;
        selectedPersonForLicense.value = null;

        if (typeof window !== 'undefined') {
            window.location.assign(path);
        }
    }

    /**
     * Take the member to another person's dashboard. This is how every caller switches (the account menu, the family
     * table, Manage license, the employee drawer), so they all get the same switch and the same failure: the navigation
     * makes the switch before it lands (`followAddress`). Resolves whether the person is on screen, so a caller keeps its
     * own UI where it is when they are not.
     */
    async function startImpersonation(personId: number): Promise<boolean> {
        // Already this person? Then there is nothing to READ — but the caller asked to be taken
        // to their dashboard, and that is a navigation, not a fetch. Returning true here left a
        // business admin who opened their own employee row on the roster with the drawer shut:
        // the portal had already loaded them into `person`, so the switch reported success and
        // went nowhere. The guard below does no work for a person already on screen, which is
        // the shortcut that was worth keeping.
        //
        // Only the person param carries over: the rest of the query belongs to the page being left.
        await router.push({ path: '/dashboard', query: { person: personId.toString() } });

        return person.value?.id === personId;
    }

    // The switch reading the person a navigation names. Navigations take turns: one that arrives while a switch is
    // reading waits for it to settle, however it went (its own navigation reports how), then asks again who its address
    // names.
    let switching: Promise<boolean> | null = null;
    // The newest navigation to reach the guard: the one that lands, unless a newer one supersedes it, and the one the
    // loader waits for. Vue Router hands the guard and the hook the same location object for one navigation, so no
    // older navigation settling meanwhile (one a switch superseded, or a switch's own, superseded by a Back) is taken
    // for it, and a navigation a guard sends elsewhere passes it on to the location it is sent to, which reaches the
    // guard in its turn.
    let newestNavigation: RouteLocationNormalized | null = null;

    /**
     * Every navigation lands on the person its address names, whether the member switched or went Back or Forward across
     * a switch.
     *
     * When the address names someone other than the person on screen, that person is read first and nothing changes
     * until the read succeeds. A read that fails leaves the person on screen and their vault as they were, tells the
     * member once, and keeps the navigation from landing, so a refused Back or Forward puts the browser back on the entry
     * it was on. A read that succeeds clears the previous person's vault, puts the new person on screen and loads their
     * vault (its reads key on the person on screen), and the navigation lands. A navigation into the business portal that
     * begins while the read is out (`invalidatePersonFetch`) leaves everything as it was too, without a word: the member
     * has gone where no impersonated person belongs.
     *
     * An address that names no one is the route middleware's to fill: it carries the person the page being left names
     * onto every navigation inside the app (`addressCarriesPerson`). A navigation that begins while a switch is under
     * way, such as a Back or Forward the member presses while the switch's loader is up, was filled from a page the
     * switch is replacing, so once the switch has settled, an address that still names no one is sent on to name the
     * person on screen, as the middleware carries them once a switch has landed. Until the boot has read the address
     * (`loadPersonFromAddress`) no one is on screen to follow it.
     *
     * The loader is on from a switch's read until the newest navigation has landed or been turned back, and a page that
     * reads for the person on screen waits it out, so the new person is read once, by the page the member lands on, and
     * the previous person not at all.
     */
    async function followAddress(to: RouteLocationNormalized): Promise<boolean | RouteLocationRaw> {
        newestNavigation = to;
        const switchUnderWay = impersonationLoader.value;
        while (switching) await Promise.allSettled([switching]);

        const named = personIdNamedBy(to.query.person);
        if (named === null && switchUnderWay && impersonating.value && addressCarriesPerson(to.path)) {
            return { path: to.path, query: { ...to.query, person: String(impersonating.value) }, hash: to.hash };
        }
        if (named === null || !person.value || named === person.value.id) return true;

        impersonationLoader.value = true;
        switching = putOnScreen(named);
        try {
            return await switching;
        } catch (error: unknown) {
            // A switch that throws stops the navigation with an error, which no navigation hook sees land.
            impersonationLoader.value = false;
            throw error;
        } finally {
            switching = null;
        }
    }

    // How many navigations into the business portal have begun (`invalidatePersonFetch`).
    let portalEntries = 0;

    /**
     * Every re-read made through {@link reread}, in the order they were asked for — identity and
     * ordering only, never which read owns a person's figures.
     *
     * The person id alone was not enough. Two reads for the SAME person overlap all the time — a
     * licence mutation re-reads the person, and so does the dashboard's scan poll as brokers
     * conclude — and whichever answers last wins the assignment, which on an out-of-order pair is
     * the OLDER record: freshly loaded licences or risk figures reverted until something read again.
     */
    let readSequence = 0;

    /**
     * How many records have been INSTALLED on screen ({@link putOnScreen},
     * {@link loadPersonFromAddress}). A read that started before an install is older than what is now
     * on screen, even for the same person: after a switch away and back, B → A → B leaves B's first
     * refresh able to answer over the record the navigation just put there.
     */
    let installGeneration = 0;

    /**
     * The newest {@link reread} for each person, so a superseded one can wait for the answer it ceded.
     *
     * Per PERSON, because one counter for everybody let an action's re-read of A disown an in-flight
     * read of B: B's answer came back, found the newest read was A's, and returned without assigning —
     * so B kept a stale risk score and its dashboard stayed on "Updating your numbers" with its scan
     * poll already stopped. Only a read of the same person can supersede a read.
     *
     * `outcome` is that call's OWN answer — the one it returns after following any supersession of its
     * own — not the read it performed. The read alone answers false the moment a newer one starts, so
     * a waiter given that saw "the figures are not current" from a call whose real answer, still out,
     * was going to be that they are. `error` is how a failure travels the same chain: the call that
     * owns the figures failed, so everyone waiting on it fails too rather than answering a quiet
     * "nothing landed" that no later read will correct.
     *
     * One entry per person read in this session, kept because a chain may still be waiting on it.
     */
    const newestRereadFor: Record<number, { sequence: number; outcome: Promise<RereadOutcome> }> = {};

    /**
     * A navigation into the business portal has begun, where no impersonated person belongs: the read a switch has out
     * puts no one on screen when it lands, and the switch resolves `false`. `middleware/routing.global.ts` says so as the
     * navigation begins, because it decides whether that navigation ends an impersonation from the person on screen, whom
     * a switch still reading has not changed yet.
     */
    function invalidatePersonFetch(): void {
        portalEntries++;
    }

    // The person a navigation names, read before anything changes, then brought on screen with their vault. A navigation
    // into the business portal that begins while the read is out leaves everything as it was.
    async function putOnScreen(personId: number): Promise<boolean> {
        const portalEntriesBefore = portalEntries;
        const next = await personService.getPerson(personId).catch((error: unknown) => {
            showError("We couldn't switch accounts", memberFacingMessage(error, 'Try again.'));
            return null;
        });
        if (!next || portalEntries !== portalEntriesBefore) {
            return false;
        }

        vaultStore.clearVaultData();
        // A navigation INSTALLS the record, so any re-read still out is older than what is on screen
        // — including one for this same person, after a switch away and back: B → A → B leaves B's
        // first refresh able to answer over the record this navigation just put there.
        installGeneration += 1;
        person.value = next;
        await vaultStore.refreshVault();
        return true;
    }

    // The newest navigation has landed, or been turned back, with no switch still reading: the loader goes.
    function releaseSwitchLoader(to: RouteLocationNormalized): void {
        if (switching || to !== newestNavigation) return;
        impersonationLoader.value = false;
    }

    const stopFollowingAddress = router.beforeEach(followAddress);
    const stopReleasingSwitchLoader = router.afterEach(releaseSwitchLoader);
    onScopeDispose(() => {
        stopFollowingAddress();
        stopReleasingSwitchLoader();
    });

    /**
     * The boot's read: the person the page's address names, or the signed-in member when it names no one. From then on
     * the address and the person on screen change together (`followAddress`), and the person on screen is read again by
     * the id held (`refreshPerson`).
     */
    async function loadPersonFromAddress(): Promise<void> {
        // Who the ADDRESS names, kept apart from who is signed in: a refusal means different things
        // for the two, and only one of them is about the session.
        const signedIn = auth.user?.identity?.id ?? null;
        const addressed = personIdNamedBy(new URLSearchParams(window.location.search).get('person'));
        // Somebody OTHER than the signed-in member: the presence of the param is not that test,
        // because an address naming the member's own id is still their own record. That URL really
        // occurs — `startImpersonation` writes `?person=<own id>` when a business admin opens their
        // own employee row, so it gets bookmarked and refreshed — and swallowing its failure would
        // leave the dashboard standing with nobody behind it.
        const otherPerson = addressed === signedIn ? null : addressed;
        const personId = addressed ?? signedIn;

        if (!personId) {
            console.warn('No person to load: the address names no one and no member is signed in');
            return;
        }

        let loaded: Person;

        try {
            loaded = await personService.getPerson(personId);
        } catch (error) {
            // The signed-in member's own record failing IS about the session — whether the address
            // left them unnamed or spelled their own id out — so it still raises and the app says so
            // the way it always has.
            if (otherPerson === null) {
                throw error;
            }

            // An address that names somebody else is staff opening a member's account, and a refusal
            // there says something about that account while the operator's own session is perfectly
            // good. Raised, it reached the boot plugin's unguarded await and Nuxt answered with its
            // fatal error page — the generic error with no person and no reason on it — so a refusal
            // could not be told from an outage, by the operator or by anyone they reported it to. It
            // is said out loud instead, carrying what the server actually answered.
            errorMessage.value = whyTheAddressedPersonDidNotLoad(error, otherPerson);
            showError('Could not open this account', errorMessage.value);

            return;
        }

        // A boot load installs the record for the address, so a re-read still out is older than it —
        // the same reason {@link putOnScreen} moves the generation.
        installGeneration += 1;
        person.value = loaded;
    }

    /**
     * What to tell an operator whose impersonated load was refused.
     *
     * The status is the useful part — a refusal and a missing record are different problems with
     * different next steps — so it is named rather than folded into one apology.
     */
    function whyTheAddressedPersonDidNotLoad(error: unknown, personId: number): string {
        const status = error instanceof ApiError ? error.code : null;

        if (status === 403) {
            return `Your account does not have access to person ${personId}.`;
        }

        if (status === 404) {
            return `Person ${personId} could not be found.`;
        }

        return `Person ${personId} could not be loaded${status ? ` (${status})` : ''}.`;
    }

    // The person an action ran on, read again once it has changed them. A switch while the read is out has put someone
    // else on screen, whose record this one does not replace.
    async function reread(personId: number): Promise<boolean> {
        const sequence = ++readSequence;
        const installedBefore = installGeneration;
        // Published before the read goes out, and settled with THIS call's answer — so a read this one
        // supersedes waits on the answer, not on the bare read. Three overlapping reads is where the
        // difference shows: the second's read answers false as soon as the third starts, and the first,
        // waiting on that read, was told the figures are stale while the second was still waiting on
        // the third and about to say they are current. Each link waits on a strictly newer read of the
        // same person, so the chain ends at the newest one and cannot close on itself.
        let settle!: (outcome: RereadOutcome) => void;
        const outcome = new Promise<RereadOutcome>((resolve) => {
            settle = resolve;
        });

        newestRereadFor[personId] = { sequence, outcome };

        try {
            const fresh = await personService.getPerson(personId);
            // Superseded only by a newer read of THIS person; a record installed since this one went
            // out is newer than it whoever it was for.
            const latest = newestRereadFor[personId];
            const superseded = latest !== undefined && latest.sequence !== sequence;

            if (person.value?.id === personId && !superseded && installGeneration === installedBefore) {
                person.value = fresh;
                settle({ assigned: true });

                return true;
            }

            // Superseded by a newer read of the SAME person: that one owns the answer, so this call
            // waits for it rather than reporting a failure it did not have. Supersession is not
            // assignment — the sequence moves when a read STARTS — and a caller that holds readiness
            // open until the figures are current needs the outcome, not the fact that somebody else is
            // asking. Its failure is this call's failure too: nothing else is coming for these figures.
            const ceded: RereadOutcome = superseded ? await latest!.outcome : { assigned: false };

            settle(ceded);

            if (ceded.error !== undefined) throw ceded.error;

            return ceded.assigned;
        } catch (e) {
            // A read that failed still cedes to a newer one for the same person, exactly as a
            // superseded one that answered does. A licence mutation re-reads the person while the
            // dashboard's terminal scan refresh is still out; the older read times out, and reporting
            // that failure made the dashboard announce that the risk score could not be updated —
            // with the mutation's read assigning the figures a moment later and the scan poll already
            // stopped, so nothing came back to correct it.
            const latest = newestRereadFor[personId];

            if (latest !== undefined && latest.sequence !== sequence && (await latest.outcome).assigned) {
                settle({ assigned: true });

                return true;
            }

            // Nothing newer answered for this person either, so the failure is this call's to report,
            // and to hand on: a call waiting on this one must not be left on a promise that never
            // settles, nor told the figures merely did not land.
            settle({ assigned: false, error: e });

            throw e;
        }
    }

    /**
     * Read the person on screen again after something outside this store changed them (a license, a signed document, a
     * vault add, two-factor), by the id already held: that is who the change was made to, whatever a navigation under
     * way names.
     */
    async function refreshPerson(): Promise<boolean> {
        if (!person.value) return false;

        // Whether the record this read brought back was ASSIGNED. A caller that waits on it to say
        // the figures are current needs to know the difference: a read the store disowned — somebody
        // else came on screen, a navigation installed a record, or a newer re-read for this same
        // person went out — left the figures to that newer read, which is still on its way.
        return await reread(person.value.id);
    }

    /**
     * Shared skeleton for person CRUD actions: guard on a loaded person, run the
     * service call, toast the outcome, optionally re-read the person it ran on.
     *
     * The action alone decides the outcome. A re-read runs once it has landed, so a read that
     * fails cannot report a saved change as failed; it is still told, because the screen keeps the
     * state from before the action, and a success beside it with nothing else said invites the
     * member to make the change again.
     *
     * A failure reads as `memberFacingMessage` has it: the API's sentence where its envelope
     * carries a reason (every deliberate refusal on the profile routes does), else
     * `options.failure`.
     */
    async function runPersonAction<T>(
        action: (personId: number) => Promise<T>,
        options: {
            success?: string;
            failure: string;
            refetch?: boolean;
            rethrow?: boolean;
            /**
             * Rejections the caller presents itself. Some refusals are expected
             * states with their own UI — an unsupported verification destination
             * offers an unverified add — and a red toast alongside that reads as
             * a fault rather than a choice.
             */
            handledByCaller?: (error: unknown) => boolean;
        }
    ): Promise<T | undefined> {
        const personId = person.value?.id;
        if (!personId) return;

        let result: T;

        try {
            result = await action(personId);
        } catch (error) {
            if (!options.handledByCaller?.(error)) {
                showError('Error', memberFacingMessage(error, options.failure));
            }

            if (options.rethrow) {
                throw error;
            }

            return undefined;
        }

        if (options.success) {
            showSuccess('Success', options.success);
        }

        if (options.refetch) {
            try {
                await reread(personId);
            } catch {
                showWarning('Page out of date', "Your change was saved, but we couldn't refresh this page. Reload it to see the change.");
            }
        }

        return result;
    }

    /**
     * Update the person data (dob, first_name, last_name, middle_name)
     * @param postData - The data to update (can be a partial of the Person type)
     */
    async function updatePerson(postData: Partial<Person>) {
        await runPersonAction((personId) => personService.updatePerson(personId, postData), {
            refetch: true,
            success: 'Person updated successfully',
            failure: 'Failed to update person',
        });
    }

    /**
     * Add an alias name to the person
     * @param postData - The alias name to add
     */
    async function addAka(postData: Aka) {
        await runPersonAction(
            async (personId) => {
                await personService.addAka(personId, postData);
                await auth.getMe();
            },
            { refetch: true, success: 'Alias name added', failure: 'Failed to add alias name', rethrow: true }
        );
    }

    /**
     * Delete an alias name from the person
     * @param akaId - The id of the alias name to delete
     */
    async function deleteAka(akaId: number) {
        await runPersonAction((personId) => personService.deleteAka(personId, akaId), {
            refetch: true,
            success: 'Alias name deleted',
            failure: 'Failed to delete alias name',
        });
    }

    /**
     * Update an alias name
     * @param akaId - The id of the alias name to update
     * @param postData - The data to update
     */
    async function updateAka(akaId: number, postData: Partial<Aka>): Promise<boolean> {
        const updated = await runPersonAction(
            async (personId) => {
                await personService.updateAka(personId, akaId, postData);
                return true;
            },
            { refetch: true, success: 'Alias name updated', failure: 'Failed to update alias name' }
        );

        return updated ?? false;
    }

    /**
     * Add an address to the person
     * @param postData - The address to add
     */
    async function addAddress(postData: Address) {
        await runPersonAction((personId) => personService.addAddress(personId, postData), {
            refetch: true,
            success: 'Address added',
            failure: 'Failed to add address',
        });
    }

    /**
     * Update an address
     * @param addressId - The id of the address to update
     * @param postData - The data to update
     */
    async function updateAddress(addressId: number, postData: Partial<Address>): Promise<boolean> {
        const updated = await runPersonAction(
            async (personId) => {
                await personService.updateAddress(personId, addressId, postData);
                return true;
            },
            { refetch: true, success: 'Address updated successfully', failure: 'Failed to update address' }
        );

        return updated ?? false;
    }

    /**
     * Delete an address from the person
     * @param addressId - The id of the address to delete
     */
    async function deleteAddress(addressId: number) {
        await runPersonAction((personId) => personService.deleteAddress(personId, addressId), {
            refetch: true,
            success: 'Address deleted',
            failure: 'Failed to delete address',
        });
    }

    /**
     * Delete a phone number from the person
     * @param phoneId - The id of the phone number to delete
     */
    async function deletePhone(phoneId: number) {
        await runPersonAction((personId) => personService.deletePhone(personId, phoneId), {
            refetch: true,
            success: 'Phone number deleted',
            failure: 'Failed to delete phone number',
        });
    }

    /**
     * Update a phone number
     * @param phoneId - The id of the phone number to update
     * @param postData - The data to update
     */
    async function updatePhone(phoneId: number, postData: { primary: boolean }) {
        await runPersonAction((personId) => personService.updatePhone(personId, phoneId, postData), {
            refetch: true,
            success: 'Phone number updated',
            failure: 'Failed to update phone number',
        });
    }

    /**
     * Send a verification code to a phone number
     * @param postData - The phone number to send the verification code to
     * @returns A toast message
     */
    async function sendPhoneVerificationCode(
        isSms: boolean,
        postData: { phone_number: string; country_code: string; method: string; fromExposure?: boolean; exposureData?: any },
        isResend: boolean = false
    ) {
        await runPersonAction(
            async (personId) => {
                await personService.sendPhoneVerificationCode(personId, postData);

                if (!isResend) {
                    useDynamicDialog(ModalNames.PHONE_VERIFICATION, postData);
                }
            },
            {
                // AddPhoneModal turns a refused destination into an offer to add
                // the number unverified, so it must not also arrive as an error.
                handledByCaller: (error) => error instanceof ApiError && error.rejected('country_code'),
                success: isSms ? 'Successfully sent phone verification code' : 'Successfully sent phone call verification code',
                failure: 'Failed to send phone verification code',
                rethrow: true,
            }
        );
    }

    /**
     * Verify the OTP code and add the phone number to the person
     * @param payload - The phone number and OTP code to verify
     */
    async function verifyPhoneOtp(payload: { country_code: string; phone_number: string; code: string }) {
        await runPersonAction((personId) => personService.verifyPhoneOtp(personId, payload), {
            refetch: true,
            success: 'Phone number added',
            failure: 'Failed to verify phone OTP',
            rethrow: true,
        });
    }

    async function addPhoneWithoutVerification(payload: { country_code: string; phone_number: string }): Promise<void> {
        await runPersonAction((personId) => personService.addPhoneWithoutVerification(personId, payload), {
            refetch: true,
            success: 'Phone number added to vault',
            failure: 'Failed to add phone number',
            rethrow: true,
        });
    }

    /**
     * Send a verification code to an email and open the PIN modal
     * @param email - The email to send the verification code to
     * @returns A toast message
     */
    async function sendEmailVerificationCode(email: string, isResend: boolean = false, additionalData?: any) {
        try {
            await personService.sendEmailVerificationCode(email);

            if (!isResend) {
                useDynamicDialog(ModalNames.EMAIL_VERIFICATION, { email, ...additionalData });
            }

            showSuccess("Success", 'Email verification code sent');
        } catch (error: unknown) {
            showError("Error", memberFacingMessage(error, 'Failed to send email verification code'));
            throw error;
        }
    }

    /**
     * Verify the OTP code and add the email to the person
     * @param payload - The email verification data to verify
     */
    async function verifyEmailOtp(payload: EmailVerificationData) {
        try {
            await runPersonAction((personId) => personService.verifyEmailOtp(personId, payload), {
                refetch: true,
                success: 'Email added',
                failure: 'Failed to verify email OTP',
                rethrow: true,
            });
        } catch (error) {
            // Beside the code, only a refusal the API worded for the member; the toast carries the rest.
            errorMessage.value = memberFacingMessage(error, '');
            throw error;
        }
    }

    /**
     * Update an email
     * @param emailId - The id of the email to update
     * @param postData - The data to update
     */
    async function updateEmail(emailId: number, postData: { primary: boolean }) {
        await runPersonAction((personId) => personService.updateEmail(personId, emailId, postData), {
            refetch: true,
            success: 'Email updated successfully',
            failure: 'Failed to update email',
        });
    }

    /**
     * Delete an email from the person
     * @param emailId - The id of the email to delete
     */
    async function deleteEmail(emailId: number) {
        await runPersonAction((personId) => personService.deleteEmail(personId, emailId), {
            refetch: true,
            success: 'Email deleted',
            failure: 'Failed to delete email',
        });
    }

    async function fetchStates(id: number) {
        return runPersonAction(() => personService.fetchStates(id), { failure: 'Failed to fetch states' });
    }

    async function fetchStatesByCode(countryCode: string) {
        return runPersonAction(() => personService.fetchStatesByCode(countryCode), { failure: 'Failed to fetch states' });
    }

    function useSearchPhrases() {
        let me = auth?.user
        if (!me) return [];

        const firstName = me.identity.first_name;
        const lastName = me.identity.last_name;
        const fullName = `${firstName} ${lastName}`;
        const city = me.identity.location?.city;
        const region = me.identity.location?.region;
        const postal = me.identity.location?.postal_code;
        const email = me.identity.email;

        return [
            fullName,
            city ? `${fullName} ${city}` : null,
            region ? `${fullName} ${region}` : null,
            postal ? `${lastName} ${postal}` : null,
            email
        ].filter(Boolean);
    }

    return {
        person,
        loadPersonFromAddress,
        refreshPerson,
        invalidatePersonFetch,
        runPersonAction,
        updatePerson,
        addAka,
        updateAka,
        deleteAka,
        addAddress,
        deleteAddress,
        deletePhone,
        deleteEmail,
        sendEmailVerificationCode,
        verifyEmailOtp,
        sendPhoneVerificationCode,
        verifyPhoneOtp,
        addPhoneWithoutVerification,
        updateAddress,
        updatePhone,
        updateEmail,
        impersonationLoader,
        imgViewerUrl,
        selectedPersonForLicense,
        impersonating,
        startImpersonation,
        endImpersonation,
        endImpersonationTo,
        fetchStates,
        fetchStatesByCode,
        errorMessage,
        useSearchPhrases
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(usePersonStore, import.meta.hot))
  }
